import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AuthService from '../../services/authService';


export const fetchUser = createAsyncThunk(
  'auth/fetchUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.getUser();
      const user = response.data.user;

      // Update localStorage to stay consistent
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('role', user.role);

      return user;
    } catch (error) {
      // Clear auth state if 401 Unauthorized
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('role');
      }

      return rejectWithValue(
        error.response?.data?.message ||
        'Failed to fetch user data'
      );
    }
  }
);

// export const loginUser = createAsyncThunk(
//   'auth/login',
//   async (credentials, { rejectWithValue }) => {
//     try {
//       const response = await AuthService.login(credentials);
//       console.log("Login response:", response);
//       // Store tokens directly in localStorage
//       const { token, user, role } = response.data;
//       localStorage.setItem('token', token);
//       localStorage.setItem('user', JSON.stringify(user));
//       localStorage.setItem('role', role); // Store role for later use
//       return { token, user}; // ✅ only return required payload
//     } catch (error) {
//       const errorMessage = error.response?.data?.message ||
//         error.response?.data?.data?.error?.[0] ||
//         error.message;
//       return rejectWithValue(errorMessage);
//     }
//   }
// );
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      console.log("Login thunk started with:", credentials);
      const response = await AuthService.login(credentials);
      console.log("AuthService response:", response);

      const data = response.data;
      console.log("Response data:", data);

      // If 2FA is required
      if (data?.user_id || (data?.data && data.data[0]?.user_id)) {
        const userId = data.user_id || data.data[0].user_id;
        const role = data.role || data.data[0].role;

        console.log("2FA required - returning:", {
          twoFARequired: true,
          userId: userId,
          role: role
        });

        return {
          twoFARequired: true,
          userId: userId,
          role: role
        };
      }

      // Normal login success
      if (data?.token && data?.user) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('role', data.user.role);

        return {
          token: data.token,
          user: data.user,
          role: data.user.role
        };
      }

      console.log("Unexpected response format");
      return rejectWithValue("Invalid login response format");

    } catch (error) {
      console.error("Login thunk error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.data?.error?.[0] ||
        error.message;
      console.error("Error message:", errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);


export const verifyTwoFA = createAsyncThunk(
  'auth/verifyTwoFA',
  async ({ userId, code }, { rejectWithValue }) => {
    try {
      const response = await AuthService.verifyTwoFA({
        user_id: userId,
        two_factor_code: code
      });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('role', user.role);

      return { token, user };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.data?.error?.[0] ||
        error.message;
      return rejectWithValue(errorMessage);
    }
  }
);
export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await AuthService.register(userData);
      console.log("Register response:", response);
      // Store tokens directly in localStorage
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('role', user.role); // Store role for later use
      return { token, user, role: user.role }; // ✅ only return required payload
    } catch (error) {
      let errorMessage;
      if (error.response?.data?.errors) {
        const [field, messages] = Object.entries(error.response.data.errors)[0];
        errorMessage = `${messages[0]}`; // "username: The username has already been taken."
      } else {
        errorMessage = error.response?.data?.message || error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const logoutUser = () => async (dispatch) => {
  try {
    await AuthService.logout(); // Call your logout API
  } finally {
    dispatch(logout()); // Clear local state regardless of API success
  }
};

export const editProfile = createAsyncThunk(
  'auth/editProfile',
  async (formData, { rejectWithValue, getState }) => {
    try {
      const response = await AuthService.editProfile(formData);

      // Extract the client data from the nested structure
      const clientData = response.data?.data?.client;

      if (!clientData) {
        throw new Error('No client data returned from API');
      }

      // Get current user data to preserve role
      const currentUser = getState().auth.user;

      // Merge updated data with existing role
      const updatedUser = {
        ...clientData,
        role: currentUser?.role // Preserve the existing role
      };

      // Update localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));

      return updatedUser;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Profile update failed"
      );
    }
  }
);

const initialState = {
  user: JSON.parse(localStorage.getItem('user')) || null,
  token: localStorage.getItem('token') || null,
  role: localStorage.getItem('role') || null,
  loading: false,
  error: null
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      // Clear all storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('role');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('role');

      state.user = null;
      state.token = null;
      state.role = null;
    },
    clearErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const { twoFARequired, user, token, role } = action.payload;

        // Handle 2FA case - don't set user/token in state yet
        if (twoFARequired) {
          // Only store what we need for 2FA verification
          // Don't set user/token since login isn't complete yet
          state.user = null;
          state.token = null;
          state.role = role; // Optional: store role if needed for UI
          return;
        }

        // Handle normal login success
        if (user && token) {
          state.user = user;
          state.token = token;
          state.role = user.role || role;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.role = action.payload.user.role;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(editProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.role = action.payload.role;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;

        // Optional: Clear auth state if fetch fails
        if (action.payload?.includes('Unauthorized')) {
          state.user = null;
          state.token = null;
          state.role = null;
        }
      })
      .addCase(verifyTwoFA.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyTwoFA.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.role = action.payload.user.role;
      })
      .addCase(verifyTwoFA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

  }
});

export const { logout, clearErrors } = authSlice.actions;
export default authSlice.reducer;